import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FiPlus, FiMinus, FiShoppingCart, FiCheckCircle, FiCreditCard } from 'react-icons/fi';

const API = '/api/restaurante/publico';

export default function CartaPublica() {
  const { qrToken } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);
  const [cesta, setCesta] = useState({}); // { articuloId: { cantidad, extras, notas } }
  const [enviando, setEnviando] = useState(false);
  const [pedido, setPedido] = useState(null); // { id, token, total, estado }
  const [pagando, setPagando] = useState(false);

  const estadoTexto = {
    pendiente_pago: 'Pendiente de pago',
    pendiente_despachar: 'Pendiente de despachar',
    en_preparacion: 'En preparación',
    servido: 'Servido',
    cancelado: 'Cancelado',
  };

  const iconoAlergeno = {
    Gluten: '🌾',
    Lactosa: '🥛',
    Pescado: '🐟',
    Huevo: '🥚',
    Soja: '🌱',
    Frutos: '🥜',
    Marisco: '🦐',
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API}/mesa/${qrToken}`);
      if (!r.ok) throw new Error((await r.json()).error || 'Mesa no encontrada');
      setInfo(await r.json());
    } catch (err) { setError(err.message); } finally { setCargando(false); }
  }, [qrToken]);

  useEffect(() => { cargar(); }, [cargar]);

  const add = (id) => setCesta((c) => ({ ...c, [id]: { ...(c[id] || { cantidad: 0, extras: [], notas: '' }), cantidad: (c[id]?.cantidad || 0) + 1 } }));
  const sub = (id) => setCesta((c) => { const actual = c[id] || { cantidad: 0, extras: [], notas: '' }; const n = actual.cantidad - 1; const nc = { ...c }; if (n <= 0) delete nc[id]; else nc[id] = { ...actual, cantidad: n }; return nc; });
  const toggleExtra = (id, extra) => setCesta((c) => {
    const actual = c[id] || { cantidad: 1, extras: [], notas: '' };
    const ya = (actual.extras || []).some((e) => e.nombre === extra.nombre);
    const extras = ya ? (actual.extras || []).filter((e) => e.nombre !== extra.nombre) : [...(actual.extras || []), { nombre: extra.nombre, precio: extra.precio || 0 }];
    return { ...c, [id]: { ...actual, extras } };
  });
  const cambiarNotas = (id, notas) => setCesta((c) => ({ ...c, [id]: { ...(c[id] || { cantidad: 1, extras: [], notas: '' }), notas } }));

  const carta = info?.carta || [];
  const porCategoria = carta.reduce((acc, a) => { (acc[a.categoria] = acc[a.categoria] || []).push(a); return acc; }, {});
  const items = Object.entries(cesta).map(([id, value]) => ({ ...carta.find((a) => a._id === id), ...value, cantidad: value?.cantidad || 1 })).filter((x) => x._id);
  const total = items.reduce((s, i) => s + (i.precio + (i.extras || []).reduce((ac, ex) => ac + (Number(ex.precio) || 0), 0)) * i.cantidad, 0);
  const numItems = items.reduce((s, i) => s + i.cantidad, 0);

  const enviar = async () => {
    if (items.length === 0) return;
    setEnviando(true);
    try {
      const r = await fetch(`${API}/mesa/${qrToken}/pedido`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lineas: items.map((i) => ({ articuloId: i._id, cantidad: i.cantidad, extras: i.extras || [], notas: i.notas || '' })) }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error');
      setPedido({ id: data.id, token: data.token, total: data.total, estado: data.estado });
      setCesta({});
    } catch (err) { alert(err.message); } finally { setEnviando(false); }
  };

  const pagar = async () => {
    setPagando(true);
    try {
      const r = await fetch(`${API}/pedido/${pedido.token}/pagar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Error');
      setPedido((p) => ({ ...p, estado: 'pendiente_despachar' }));
      alert('¡Pago confirmado! El pedido ya está listo para despachar.');
    } catch (err) { alert(err.message); } finally { setPagando(false); }
  };

  if (cargando) return <div className="carta-publica"><p className="cp-info">Cargando carta…</p></div>;
  if (error) return <div className="carta-publica"><p className="cp-error">{error}</p></div>;

  return (
    <div className="carta-publica">
      <header className="cp-header">
        {info.empresa.logo && <img src={info.empresa.logo} alt="" className="cp-logo" />}
        <h1>{info.empresa.nombre}</h1>
        <p className="cp-mesa">Mesa {info.mesa.numero}{info.mesa.nombre ? ` · ${info.mesa.nombre}` : ''}</p>
      </header>

      {pedido ? (
        <div className="cp-confirmacion">
          <FiCheckCircle className="cp-ok-icon" />
          <h2>¡Pedido recibido!</h2>
          <p>Total: <strong>{pedido.total.toFixed(2)} €</strong></p>
          <p className="cp-estado">Estado: {estadoTexto[pedido.estado] || pedido.estado}</p>
          {info.pagoOnline && pedido.estado === 'pendiente_pago' && (
            <button className="cp-btn cp-btn-pago" onClick={pagar} disabled={pagando}>
              <FiCreditCard /> {pagando ? 'Procesando…' : `Pagar ${pedido.total.toFixed(2)} €`}
            </button>
          )}
          <button className="cp-btn cp-btn-sec" onClick={() => setPedido(null)}>Hacer otro pedido</button>
        </div>
      ) : (
        <>
          <main className="cp-carta">
            {Object.entries(porCategoria).map(([cat, arts]) => (
              <section key={cat}>
                <h3 className="cp-categoria">{cat}</h3>
                {arts.map((a) => {
                  const seleccion = cesta[a._id];
                  const cantidad = seleccion?.cantidad || 0;
                  return (
                    <div key={a._id} className="cp-articulo">
                      {a.foto && <img src={a.foto} alt={a.nombre} className="cp-art-img" />}
                      <div className="cp-art-info">
                        <strong>{a.nombre}</strong>
                        {a.descripcion && <span>{a.descripcion}</span>}
                        {a.descripcionLarga && <span className="cp-descripcion-larga">{a.descripcionLarga}</span>}
                        {(a.subproductos || []).length > 0 && (
                          <span className="cp-subproductos">Extras sugeridos: {a.subproductos.map((s) => `${s.nombre}${s.precio ? ` (+${s.precio.toFixed(2)} €)` : ''}`).join(', ')}</span>
                        )}
                        {(a.extras || []).length > 0 && (
                          <div className="cp-extras">
                            <span>Elige extras:</span>
                            {a.extras.map((extra) => {
                              const selected = (seleccion?.extras || []).some((e) => e.nombre === extra.nombre);
                              return (
                                <label key={`${a._id}-${extra.nombre}`} className="cp-extra-option">
                                  <input type="checkbox" checked={selected} onChange={() => toggleExtra(a._id, extra)} />
                                  <span>{extra.nombre}{extra.precio ? ` (+${extra.precio.toFixed(2)} €)` : ''}</span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                        {(a.alergenos || []).length > 0 && (
                          <span className="cp-alergenos">Alérgenos: {a.alergenos.map((al) => `${iconoAlergeno[al] || '⚠️'} ${al}`).join(' · ')}</span>
                        )}
                        <span className="cp-precio">{a.precio.toFixed(2)} €</span>
                        <span className="cp-stock">{Number(a.stock ?? 0) > 0 ? `Quedan ${a.stock} uds.` : 'Sin stock'}</span>
                        <textarea className="cp-notas" rows="2" value={seleccion?.notas || ''} onChange={(e) => cambiarNotas(a._id, e.target.value)} placeholder="Notas para la cocina" />
                      </div>
                      <div className="cp-art-ctrl">
                        {cantidad > 0 ? (
                          <>
                            <button onClick={() => sub(a._id)}><FiMinus /></button>
                            <span>{cantidad}</span>
                            <button onClick={() => add(a._id)}><FiPlus /></button>
                          </>
                        ) : <button className="cp-add" onClick={() => add(a._id)}><FiPlus /></button>}
                      </div>
                    </div>
                  );
                })}
              </section>
            ))}
            {carta.length === 0 && <p className="cp-info">No hay artículos disponibles.</p>}
          </main>

          {numItems > 0 && (
            <div className="cp-cesta-bar">
              <div><FiShoppingCart /> {numItems} artículo(s) · <strong>{total.toFixed(2)} €</strong></div>
              <button className="cp-btn" onClick={enviar} disabled={enviando}>{enviando ? 'Enviando…' : 'Pedir'}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
